/**
 * Accounts.tsx — Admin accounts list with inline editing (ARCH §9.4, D-18).
 *
 * - GET /accounts → lists all accounts with validation_status + connection_status
 * - PATCH /accounts/:id → update display_label/notes/tags/is_active
 * - No credentials displayed ever (ARCH §10.2)
 */

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountsApi, ApiRequestError } from '../lib/api-client.js';
import type { AccountDto, AccountPatchDto, RemovedAccountDto, PersonaAge, PersonaMood, CreatorLabelRulesResult } from '@crm/types';
import { CREATOR_LABELS, CREATOR_LABEL_META } from '@crm/types';
import { HealthBadge } from '../components/HealthBadge.js';
import { BulkPasswordDialog } from '../components/BulkPasswordDialog.js';
import {
  Button,
  Badge,
  Input,
  Textarea,
  Dialog,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  useToast,
} from '@crm/ui';
import { formatBerlin, formatBerlinDate } from '../lib/time.js';

// ---------------------------------------------------------------------------
// Validation status badge
// ---------------------------------------------------------------------------

function ValidationBadge({
  status,
}: {
  status: AccountDto['validationStatus'];
}) {
  switch (status) {
    case 'valid':
      return <Badge variant="success">Valid</Badge>;
    case 'invalid':
      return <Badge variant="danger">Invalid</Badge>;
    case 'rate_limited':
      return <Badge variant="warning">Rate limited</Badge>;
    case 'network_error':
      return <Badge variant="warning">Net error</Badge>;
    case 'pending':
    default:
      return <Badge variant="outline">Pending</Badge>;
  }
}

// ---------------------------------------------------------------------------
// Translation persona (per creator — 2026-09-16)
// ---------------------------------------------------------------------------

/** Max voice-note length — mirrors PERSONA_VOICE_MAX_CHARS on the API + the DB CHECK. */
const PERSONA_VOICE_MAX = 600;

const PERSONA_AGE_OPTIONS: Array<{ value: PersonaAge | ''; label: string }> = [
  { value: '', label: 'Default (young)' },
  { value: 'young', label: 'Young — early 20s, playful' },
  { value: 'mature', label: 'Mature — late 30s, composed' },
];

const PERSONA_MOOD_OPTIONS: Array<{ value: PersonaMood | ''; label: string }> = [
  { value: '', label: 'Default (flirty)' },
  { value: 'casual', label: 'Casual — relaxed, friendly' },
  { value: 'flirty', label: 'Flirty — playful, teasing' },
  { value: 'seductive', label: 'Seductive — charged, intense' },
];

/** Toast text after a label change: the rule for new posts plus what happened to queued ones. */
function labelRulesMessage(r: CreatorLabelRulesResult): string {
  const rule = r.requiredCategories.length
    ? `Every new post gets the ${r.requiredCategories.join(', ')} category.`
    : 'No label rules apply to new posts.';
  const fixed = r.updatedPosts ? ` ${r.updatedPosts} queued post${r.updatedPosts === 1 ? '' : 's'} updated in the CRM.` : '';
  const native = r.postsOnMaloumMissing
    ? ` ${r.postsOnMaloumMissing} post${r.postsOnMaloumMissing === 1 ? '' : 's'} already scheduled on Maloum still lack it — edit those on Maloum.`
    : '';
  return `Account updated. ${rule}${fixed}${native}`;
}
const SELECT_CLASS =
  'w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60';

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------

interface EditDialogProps {
  account: AccountDto;
  onClose: () => void;
}

function EditDialog({ account, onClose }: EditDialogProps) {
  const { addToast } = useToast();
  const qc = useQueryClient();

  const [form, setForm] = React.useState<AccountPatchDto>({
    displayLabel: account.displayLabel,
    notes: account.notes ?? '',
    tags: [...account.tags],
    isActive: account.isActive,
    personaAge: account.personaAge,
    personaMood: account.personaMood,
    personaVoice: account.personaVoice ?? '',
    labels: [...account.labels],
  });
  const [tagsInput, setTagsInput] = React.useState(
    account.tags.join(', '),
  );

  const voiceLen = (form.personaVoice ?? '').length;

  const mut = useMutation({
    mutationFn: () => {
      const patch: AccountPatchDto = {
        ...form,
        tags: tagsInput
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        notes: form.notes ?? undefined,
        // '' from the selects / an emptied textarea → null = reset to the engine default.
        personaAge: form.personaAge || null,
        personaMood: form.personaMood || null,
        personaVoice: (form.personaVoice ?? '').trim() || null,
      };
      return accountsApi.patch(account.id, patch);
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['post-bot'] });
      addToast(
        result.labelRules ? labelRulesMessage(result.labelRules) : 'Account updated.',
        result.labelRules?.postsOnMaloumMissing ? 'warning' : 'success',
      );
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiRequestError
          ? err.message
          : 'Update failed';
      addToast(msg, 'danger');
    },
  });

  return (
    <Dialog open title={`Edit: ${account.displayLabel}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="flex flex-col gap-3"
      >
        <Input
          label="Display Label"
          value={form.displayLabel ?? ''}
          onChange={(e) =>
            setForm((p) => ({ ...p, displayLabel: e.target.value }))
          }
          disabled={mut.isPending}
        />
        <Textarea
          label="Notes"
          value={form.notes ?? ''}
          onChange={(e) =>
            setForm((p) => ({ ...p, notes: e.target.value }))
          }
          disabled={mut.isPending}
        />
        <Input
          label="Tags (comma-separated)"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          disabled={mut.isPending}
        />
        <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
          <input
            type="checkbox"
            checked={form.isActive ?? true}
            onChange={(e) =>
              setForm((p) => ({ ...p, isActive: e.target.checked }))
            }
            disabled={mut.isPending}
            className="accent-accent"
          />
          Active
        </label>

        <fieldset className="flex flex-col gap-2 border-t border-border pt-3 mt-1">
          <legend className="text-sm font-medium text-text">Creator labels</legend>
          <p className="text-xs text-text-muted -mt-1">
            Rules that apply to every account of <span className="font-medium">{account.creatorName}</span>.
            Changing a label also updates this creator’s posts still queued in the CRM.
          </p>
          {CREATOR_LABELS.map((label) => (
            <label key={label} className="flex items-start gap-2 text-sm text-text cursor-pointer">
              <input
                type="checkbox"
                className="accent-accent mt-0.5"
                checked={form.labels?.includes(label) ?? false}
                disabled={mut.isPending}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    labels: e.target.checked
                      ? [...(p.labels ?? []).filter((l) => l !== label), label]
                      : (p.labels ?? []).filter((l) => l !== label),
                  }))
                }
              />
              <span>
                <span className="font-medium">{CREATOR_LABEL_META[label].title}</span>{' '}
                <span className="text-xs text-text-muted">— {CREATOR_LABEL_META[label].description}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <fieldset className="flex flex-col gap-3 border-t border-border pt-3 mt-1">
          <legend className="text-sm font-medium text-text">Translation persona</legend>
          <p className="text-xs text-text-muted -mt-1">
            How the German replies for <span className="font-medium">{account.creatorName}</span>{' '}
            sound. Applies to every account of this creator.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm text-text">
              <span className="text-xs text-text-muted">Age</span>
              <select
                className={SELECT_CLASS}
                value={form.personaAge ?? ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    personaAge: (e.target.value || null) as PersonaAge | null,
                  }))
                }
                disabled={mut.isPending}
              >
                {PERSONA_AGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-text">
              <span className="text-xs text-text-muted">Mood</span>
              <select
                className={SELECT_CLASS}
                value={form.personaMood ?? ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    personaMood: (e.target.value || null) as PersonaMood | null,
                  }))
                }
                disabled={mut.isPending}
              >
                {PERSONA_MOOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {/* Label text is kept STABLE (the counter lives below): Textarea derives its
              id from the label, so a counter in the label would change the id — and
              break the label↔field association — on every keystroke. */}
          <div className="flex flex-col gap-1">
            <Textarea
              id="persona-voice"
              label="Voice notes for the translator"
              placeholder="e.g. Calls fans “Daddy”, never “Schatz”. Very explicit, no cute emoji. Bavarian slang is fine."
              value={form.personaVoice ?? ''}
              maxLength={PERSONA_VOICE_MAX}
              onChange={(e) =>
                setForm((p) => ({ ...p, personaVoice: e.target.value }))
              }
              disabled={mut.isPending}
            />
            <p className="text-xs text-text-muted self-end" aria-hidden="true">
              {voiceLen}/{PERSONA_VOICE_MAX}
            </p>
          </div>
        </fieldset>

        <div className="flex gap-2 justify-end mt-2">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" loading={mut.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Remove confirmation dialog
// ---------------------------------------------------------------------------

interface RemoveDialogProps {
  account: AccountDto;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

function RemoveDialog({ account, onClose, onConfirm, isPending }: RemoveDialogProps) {
  return (
    <Dialog open onClose={onClose} title={`Remove ${account.displayLabel}?`}>
      <div className="flex flex-col gap-4 p-4" role="alertdialog" aria-modal="true">
        <p className="text-sm text-text">
          This removes <span className="font-medium">{account.displayLabel}</span>{' '}
          ({account.email}) from the CRM, clears its chatter assignments, and hides
          its earnings from the Revenue dashboard. Stored credentials and earnings
          data are not deleted, and this can be reversed by support.
        </p>
        <div className="flex justify-end gap-2">
          {/* Cancel autoFocused per alertdialog pattern */}
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={isPending}
            onClick={onConfirm}
            aria-label={`Confirm remove ${account.displayLabel}`}
          >
            Remove
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Wipe-earnings confirmation dialog (owner decision 2026-07-03)
// ---------------------------------------------------------------------------

/** T-07-05-02 formatter shape: €(minor/100), never re-multiply. */
function formatEur(minor: number): string {
  return `€${(minor / 100).toFixed(2)}`;
}

interface WipeDialogProps {
  account: RemovedAccountDto;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

function WipeDialog({ account, onClose, onConfirm, isPending }: WipeDialogProps) {
  return (
    <Dialog open onClose={onClose} title={`Wipe earnings of ${account.displayLabel}?`}>
      <div className="flex flex-col gap-4 p-4" role="alertdialog" aria-modal="true">
        <p className="text-sm text-text">
          This permanently deletes{' '}
          <span className="font-medium">
            {account.txCount} transaction row{account.txCount === 1 ? '' : 's'}
          </span>{' '}
          ({formatEur(account.earningsGrossMinor)} earnings) stored for{' '}
          <span className="font-medium">{account.displayLabel}</span> ({account.email}).
          This is the GDPR cleanup step for a removed account and{' '}
          <span className="font-medium">cannot be undone</span>. The action is
          recorded in the audit log.
        </p>
        <div className="flex justify-end gap-2">
          {/* Cancel autoFocused per alertdialog pattern */}
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={isPending}
            onClick={onConfirm}
            aria-label={`Confirm wipe earnings of ${account.displayLabel}`}
          >
            Wipe earnings
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Removed accounts section (owner decision 2026-07-03: hide + explicit wipe)
// ---------------------------------------------------------------------------

function RemovedAccountsCard() {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [wipeTarget, setWipeTarget] = React.useState<RemovedAccountDto | null>(null);

  const { data } = useQuery({
    queryKey: ['accounts', 'removed'],
    queryFn: () => accountsApi.listRemoved(),
    refetchInterval: 30_000,
  });

  const wipeMut = useMutation({
    mutationFn: (id: string) => accountsApi.wipeEarnings(id),
    onSuccess: (result) => {
      const name = wipeTarget?.displayLabel ?? 'Account';
      qc.invalidateQueries({ queryKey: ['accounts'] });
      addToast(
        `${name}: ${result.deletedRows} transaction row${result.deletedRows === 1 ? '' : 's'} wiped.`,
        'success',
      );
      setWipeTarget(null);
    },
    onError: (err) => {
      addToast(err instanceof ApiRequestError ? err.message : 'Wipe failed', 'danger');
      setWipeTarget(null);
    },
  });

  // Nothing removed → no section at all (the common case).
  if (!data || data.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Removed account{data.length === 1 ? '' : 's'} ({data.length})
        </CardTitle>
        <p className="text-sm text-text-muted mt-0.5">
          Hidden from all revenue views. Wiping permanently deletes an account's
          stored transaction data (GDPR cleanup) — it cannot be undone.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Removed</TableHead>
              <TableHead>Stored earnings</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((acc) => (
              <TableRow key={acc.id}>
                <TableCell className="font-medium">{acc.displayLabel}</TableCell>
                <TableCell className="font-mono text-xs text-text-muted">
                  {acc.email}
                </TableCell>
                <TableCell className="text-xs text-text-muted whitespace-nowrap">
                  {formatBerlin(acc.removedAt)}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {acc.txCount === 0 ? (
                    <Badge variant="outline">No earnings data</Badge>
                  ) : (
                    <span className="text-sm">
                      {acc.txCount} tx · {formatEur(acc.earningsGrossMinor)}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-danger"
                      disabled={acc.txCount === 0}
                      onClick={() => setWipeTarget(acc)}
                    >
                      Wipe earnings
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {wipeTarget && (
        <WipeDialog
          account={wipeTarget}
          onClose={() => setWipeTarget(null)}
          onConfirm={() => wipeMut.mutate(wipeTarget.id)}
          isPending={wipeMut.isPending}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Re-auth dialog — reconnect a stuck / hard-revoked account (POST /accounts/:id/reauth).
// Tries an automatic refresh-token rotation first; if the session is terminally
// hard-revoked (the worker is stuck on Connecting / Needs Re-auth because the refresh
// token was revoked on Maloum's side), the admin supplies the creator's current Maloum
// password to re-mint. The password is transit-only (never persisted) and never seen here.
// ---------------------------------------------------------------------------

interface ReauthDialogProps {
  account: AccountDto;
  onClose: () => void;
}

function ReauthDialog({ account, onClose }: ReauthDialogProps) {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [password, setPassword] = React.useState('');
  const [needsPassword, setNeedsPassword] = React.useState(false);

  const mut = useMutation({
    mutationFn: () =>
      accountsApi.reauth(account.id, {
        password: password.trim() ? password : undefined,
      }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      addToast(
        result.mechanism === 'refresh'
          ? `${account.displayLabel}: session refreshed — reconnecting.`
          : `${account.displayLabel}: re-authenticated — reconnecting.`,
        'success',
      );
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiRequestError ? err.message : 'Re-auth failed';
      // Terminal hard-revoke with no password supplied → prompt for the password inline
      // (keep the dialog open) rather than toasting a dead-end error.
      if (/password is required/i.test(msg)) {
        setNeedsPassword(true);
        return;
      }
      addToast(msg, 'danger');
    },
  });

  return (
    <Dialog open title={`Re-auth: ${account.displayLabel}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="flex flex-col gap-3"
      >
        <p className="text-sm text-text-muted">
          Reconnects{' '}
          <span className="font-medium text-text">{account.displayLabel}</span>{' '}
          (<span className="font-mono text-xs">{account.email}</span>). This first tries to
          refresh the session automatically. If the session is hard-revoked (stuck on
          Connecting / Needs Re-auth), enter the creator&apos;s current Maloum password to
          re-mint it.
        </p>

        {needsPassword && (
          <p className="text-sm text-danger">
            Automatic refresh failed — this session is hard-revoked. Enter the
            creator&apos;s current Maloum password and click Reconnect again.
          </p>
        )}

        <Input
          label="Maloum password"
          type="password"
          autoComplete="off"
          placeholder="Required only if automatic refresh fails"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus={needsPassword}
        />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" loading={mut.isPending}>
            Reconnect
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function Accounts() {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<AccountDto | null>(null);
  const [reauthTarget, setReauthTarget] = React.useState<AccountDto | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<AccountDto | null>(null);
  const [bulkPasswordOpen, setBulkPasswordOpen] = React.useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
    refetchInterval: 30_000,
  });

  const removeMut = useMutation({
    mutationFn: (id: string) => accountsApi.remove(id),
    onSuccess: () => {
      const name = removeTarget?.displayLabel ?? 'Account';
      qc.invalidateQueries({ queryKey: ['accounts'] });
      addToast(`${name} removed.`, 'success');
      setRemoveTarget(null);
    },
    onError: (err) => {
      addToast(err instanceof ApiRequestError ? err.message : 'Remove failed', 'danger');
      setRemoveTarget(null);
    },
  });

  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-text">Accounts</h1>
          <p className="text-sm text-text-muted mt-0.5">
            All onboarded accounts — validation status and live health.
          </p>
        </div>
        {/* Fleet-wide password change → every session hard-revokes (Needs Re-auth). Paste the
            new logins once instead of 40× Re-auth (owner ask 2026-09-22). */}
        <Button
          variant="outline"
          size="sm"
          disabled={!data || data.length === 0}
          onClick={() => setBulkPasswordOpen(true)}
        >
          Bulk password update
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {data ? `${data.length} account${data.length === 1 ? '' : 's'}` : 'Accounts'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && (
            <div className="p-4 text-sm text-text-muted">Loading…</div>
          )}
          {isError && (
            <div className="p-4 text-sm text-danger">
              {(error as Error).message}
            </div>
          )}
          {data && data.length === 0 && (
            <div className="p-4 text-sm text-text-muted">
              No accounts yet.{' '}
              <a href="/onboarding" className="text-accent underline">
                Onboard one
              </a>
              .
            </div>
          )}
          {data && data.length > 0 && (
            <Table>
              <TableHeader>
                {/* Owner ask 2026-09-30: the table must fit without horizontal scroll. Label
                    (= creator name in practice), Persona and Active dropped as columns — the
                    label shows under the creator only when it differs, an inactive account
                    gets an "Inactive" badge, persona stays in the Edit dialog. */}
                <TableRow>
                  <TableHead>Creator</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Validation</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead>Labels</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((acc) => (
                  <TableRow key={acc.id}>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium">{acc.creatorName}</span>
                        {!acc.isActive && <Badge variant="danger">Inactive</Badge>}
                      </div>
                      {acc.displayLabel !== acc.creatorName && (
                        <p className="text-xs text-text-muted">{acc.displayLabel}</p>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-text-muted break-all">
                      {acc.email}
                    </TableCell>
                    <TableCell>
                      <ValidationBadge status={acc.validationStatus} />
                    </TableCell>
                    <TableCell>
                      <HealthBadge
                        accountId={acc.id}
                        initialState={acc.connectionStatus}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {acc.labels.map((l) => (
                          <span key={l} title={CREATOR_LABEL_META[l].description}>
                            <Badge variant="success" className="text-[10px]">
                              {CREATOR_LABEL_META[l].title}
                            </Badge>
                          </span>
                        ))}
                        {acc.tags.map((t) => (
                          <Badge key={t} variant="outline" className="text-[10px]">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell
                      className="text-xs text-text-muted whitespace-nowrap"
                      title={formatBerlin(acc.createdAt)}
                    >
                      {formatBerlinDate(acc.createdAt)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(acc)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-accent"
                          onClick={() => setReauthTarget(acc)}
                        >
                          Re-auth
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-danger"
                          onClick={() => setRemoveTarget(acc)}
                        >
                          Remove
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RemovedAccountsCard />

      {editing && (
        <EditDialog account={editing} onClose={() => setEditing(null)} />
      )}

      {reauthTarget && (
        <ReauthDialog account={reauthTarget} onClose={() => setReauthTarget(null)} />
      )}

      {bulkPasswordOpen && data && (
        <BulkPasswordDialog accounts={data} onClose={() => setBulkPasswordOpen(false)} />
      )}

      {removeTarget && (
        <RemoveDialog
          account={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onConfirm={() => removeMut.mutate(removeTarget.id)}
          isPending={removeMut.isPending}
        />
      )}
    </div>
  );
}
